<script setup>
import { ref, reactive, watch } from 'vue'

const props = defineProps({
  show: Boolean,
  postData: Object
})

const emit = defineEmits(['update:show', 'confirm'])

const form = reactive({
  isTop: 1,
  topSort: 0,
  topExpireTime: ''
})
const submitting = ref(false)

watch(() => props.show, (val) => {
  if (val && props.postData) {
    form.isTop = props.postData.isTop === 1 ? 0 : 1
    form.topSort = props.postData.topSort || 0
    form.topExpireTime = props.postData.topExpireTime
      ? props.postData.topExpireTime.replace('T', ' ').substring(0, 16)
      : ''
  }
})

const isSettingTop = () => form.isTop === 1

const handleClose = () => {
  emit('update:show', false)
}

const handleSubmit = () => {
  submitting.value = true
  emit('confirm', {
    id: props.postData.id,
    isTop: form.isTop,
    topSort: isSettingTop() ? form.topSort : undefined,
    topExpireTime: isSettingTop() && form.topExpireTime ? form.topExpireTime : undefined
  })
}

const done = () => {
  submitting.value = false
  handleClose()
}

defineExpose({ done })
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="handleClose"></div>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md">
            <!-- 头部 -->
            <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 class="text-lg font-semibold text-slate-900">
                {{ postData?.isTop === 1 ? '取消置顶' : '设置置顶' }}
              </h3>
              <button
                @click="handleClose"
                class="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <!-- 内容 -->
            <div class="p-6 space-y-4">
              <!-- 取消置顶时的提示 -->
              <template v-if="!isSettingTop()">
                <p class="text-sm text-slate-600">确定要取消该动态的置顶状态吗？</p>
              </template>

              <!-- 设置置顶时的表单 -->
              <template v-else>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1">排序值</label>
                  <input
                    v-model.number="form.topSort"
                    type="number"
                    min="0"
                    placeholder="值越小越靠前"
                    class="form-input form-number w-full"
                  />
                  <p class="text-xs text-slate-400 mt-1">值越小排序越靠前</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-700 mb-1">过期时间</label>
                  <input
                    v-model="form.topExpireTime"
                    type="datetime-local"
                    class="w-full form-date"
                  />
                  <p class="text-xs text-slate-400 mt-1">不填则永不过期</p>
                </div>
              </template>
            </div>

            <!-- 底部 -->
            <div class="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                @click="handleClose"
                class="btn-secondary"
              >
                取消
              </button>
              <button
                @click="handleSubmit"
                :disabled="submitting"
                class="btn-primary"
              >
                {{ submitting ? '提交中...' : '确定' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
